import { LoadingOverlay, Modal, Space } from '@mantine/core';
import type { Dispatch, SetStateAction } from 'react';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { useModal } from '../../../hooks/useModal';
import { mutate } from '../../../util/api/mutate';
import { useVatRate } from '../../vat-rates/hooks/useVatRate';
import { useUpsertPriceForm } from '../hooks/useUpsertPriceForm';
import type {
	BranchStockDto,
	UpsertBranchStockPriceDto,
} from '../types/BranchStockDto';
import { UpsertPriceForm } from './UpsertPriceForm';

interface EditBranchStockPriceModalProps {
	close: () => void;
	isOpened: boolean;
	updateProductRecord: Dispatch<SetStateAction<BranchStockDto>>;
	encodedSkuid: string;
	branchId: string;
	defaultBranchStockPrice: UpsertBranchStockPriceDto;
	vatRateId?: string;
}

export function EditBranchStockPriceModal({
	isOpened,
	close,
	updateProductRecord,
	defaultBranchStockPrice,
	encodedSkuid,
	branchId,
	vatRateId,
}: EditBranchStockPriceModalProps) {
	const { organisation } = useParams();

	const successModal = useModal();

	const vatRate = useVatRate(vatRateId);

	const { form, inputs } = useUpsertPriceForm(defaultBranchStockPrice, vatRate);

	const editBranchStockPriceMutation = mutate<
		UpsertBranchStockPriceDto,
		BranchStockDto
	>(
		`${organisation}/branch-stock/edit-price/${branchId}/${encodedSkuid}`,
		inputs,
	);

	const resetAndClose = () => {
		form.reset();
		successModal.close();

		close();
	};

	const complete = () => {
		if (editBranchStockPriceMutation.data) {
			form.setInitialValues(inputs);
			updateProductRecord(editBranchStockPriceMutation.data.value);
		}

		resetAndClose();
	};

	return (
		<>
			<Modal
				opened={isOpened}
				onClose={resetAndClose}
				title={<Title label={'Edit Branch Price'} />}
				size={'lg'}
				centered
			>
				<LoadingOverlay visible={editBranchStockPriceMutation.isLoading} />
				<SmallModal
					isOpen={successModal.isOpen}
					close={complete}
					title={'Branch Price Edited!'}
					body={
						'A branch price has been successfully edited. Click Ok to continue...'
					}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await editBranchStockPriceMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								successModal.open();
							}
						});
					})}
				>
					<UpsertPriceForm form={form} />
					<FormButtons close={resetAndClose} reducePaddingTop />
				</form>
			</Modal>
		</>
	);
}
